import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Heading, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import LandingFooter from '../../components/LandingFooter/LandingFooter';
import SubPageHero from '../../components/SubPageHero/SubPageHero';
import './ProductQentorosPlatform.css';
import Icon from '../../components/ui/Icon';
import Seo from '../../components/Seo/Seo';

const MotionBox = motion(Box);
const publicUrl = (import.meta.env.BASE_URL || '').replace(/\/$/, '');
const heroImageUrl = `${publicUrl}/images/cleanroom.jpg`;

export default function ProductQentorosPlatform() {
  const navigate = useNavigate();

  return (
    <div className="product-platform-page">
      <Seo
        title="The Qentoros Platform | Equine Clinical Trial Management"
        description="The Qentoros platform runs equine clinical trials end to end — trials, patients, compliance, and documentation in one place, built for veterinarians and sponsors."
        path="/products/qentoros-platform"
      />
      <LandingHeader />

      <main id="main" tabIndex={-1}>
        {/* Hero */}
        <SubPageHero
          eyebrow="The platform"
          title="The platform for running equine clinical trials"
          subtitle="Log in to access trials, manage patients, and keep compliance and documentation in one place, built for veterinarians and sponsors."
          backgroundImage={heroImageUrl}
          // A platform demo is a conversation, not a shop enrollment, so this
          // routes to the contact form rather than /enroll.
          cta={{ label: 'Request a Demo', onClick: () => navigate('/contact') }}
          secondaryCta={{ label: 'Log in', onClick: () => navigate('/login') }}
        />

        {/* Trial administration — low-fidelity wireframe */}
        <section id="trial-admin" className="product-platform-section" aria-labelledby="trial-admin-heading">
          <div className="product-platform-split">
            <MotionBox
              className="product-platform-split-copy"
              initial={{ opacity: 0, x: -16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4 }}
            >
              <p className="eyebrow">Trial administration</p>
              <div className="section-accent" aria-hidden="true" />
              <h2 id="trial-admin-heading">Discover trials, request access, manage patients</h2>
              <p>
                Browse available clinical trials, request access for your practice, and manage enrolled patients and protocols from a single dashboard.
              </p>
            </MotionBox>
            <MotionBox
              className="product-wireframe-wrapper"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: 0.08 }}
            >
              <p className="product-wireframe-screen-title">Clinical Trials</p>
              <div className="product-wireframe-cards" aria-hidden="true">
                <div className="product-wireframe-card">
                  <div className="product-wireframe-card-top">
                    <span className="product-wireframe-card-name">Bio-Ply</span>
                    <span className="product-wireframe-card-badge product-wireframe-card-badge-active">Active</span>
                  </div>
                  <p className="product-wireframe-card-desc">Clinical trial for BIO-PLY™ platelet-rich plasma treatment</p>
                  <div className="product-wireframe-card-meta">
                    <span className="product-wireframe-card-meta-item"><Icon name="pets" /> Equine</span>
                    <span className="product-wireframe-card-meta-item"><Icon name="people" /> 12 Patients</span>
                    <span className="product-wireframe-card-meta-item"><Icon name="event" /> Phase II</span>
                  </div>
                  <span className="product-wireframe-card-btn">View Details</span>
                </div>
                <div className="product-wireframe-card">
                  <div className="product-wireframe-card-top">
                    <span className="product-wireframe-card-name">Bio-Ply (Unofficial)</span>
                    <span className="product-wireframe-card-badge product-wireframe-card-badge-active">Active</span>
                  </div>
                  <p className="product-wireframe-card-desc">Unofficial study for BIO-PLY™ in canine patients</p>
                  <div className="product-wireframe-card-meta">
                    <span className="product-wireframe-card-meta-item"><Icon name="pets" /> Canine</span>
                    <span className="product-wireframe-card-meta-item"><Icon name="people" /> 3 Patients</span>
                    <span className="product-wireframe-card-meta-item"><Icon name="science" /> Study</span>
                  </div>
                  <span className="product-wireframe-card-btn">View Details</span>
                </div>
              </div>
            </MotionBox>
          </div>
        </section>

        {/* FDA / regulatory and compliance — low-fidelity wireframe */}
        <section id="compliance" className="product-platform-section product-platform-split-reverse" aria-labelledby="compliance-heading">
          <div className="product-platform-split">
            <MotionBox
              className="product-wireframe-wrapper product-platform-wireframe-first"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4 }}
            >
              <p className="product-wireframe-screen-title">Forms & Deadlines</p>
              <div className="product-wireframe-forms" aria-hidden="true">
                <div className="product-wireframe-form-card product-wireframe-form-warning">
                  <Icon name="error_outline" className="product-wireframe-form-icon" />
                  <span className="product-wireframe-form-label">Protocol Deviation</span>
                </div>
                <div className="product-wireframe-form-card product-wireframe-form-danger">
                  <Icon name="warning" className="product-wireframe-form-icon" />
                  <span className="product-wireframe-form-label">Adverse Event</span>
                </div>
                <div className="product-wireframe-form-card product-wireframe-form-danger">
                  <Icon name="exit_to_app" className="product-wireframe-form-icon" />
                  <span className="product-wireframe-form-label">Early Exit</span>
                </div>
                <div className="product-wireframe-form-card product-wireframe-form-info">
                  <Icon name="phone" className="product-wireframe-form-icon" />
                  <span className="product-wireframe-form-label">Communication Record</span>
                </div>
                <div className="product-wireframe-form-card product-wireframe-form-neutral">
                  <Icon name="note" className="product-wireframe-form-icon" />
                  <span className="product-wireframe-form-label">Note to File</span>
                </div>
              </div>
              <div className="product-wireframe-timeline" style={{ marginTop: 'var(--spacing-lg)' }} aria-hidden="true">
                <p className="product-wireframe-timeline-heading">Upcoming tasks</p>
                <div className="product-wireframe-timeline-row">
                  <span className="product-wireframe-timeline-date">Mar 2</span>
                  <span className="product-wireframe-timeline-bar product-wireframe-timeline-bar-label">Day 0 – Initial treatment · Luna</span>
                </div>
                <div className="product-wireframe-timeline-row">
                  <span className="product-wireframe-timeline-date">Mar 9</span>
                  <span className="product-wireframe-timeline-bar product-wireframe-timeline-bar-label">Day 7 – Follow-up · Thunder</span>
                </div>
                <div className="product-wireframe-timeline-row">
                  <span className="product-wireframe-timeline-date">Mar 16</span>
                  <span className="product-wireframe-timeline-bar product-wireframe-timeline-bar-label">Day 14 – Follow-up · Luna</span>
                </div>
              </div>
            </MotionBox>
            <MotionBox
              className="product-platform-split-copy product-platform-copy-second"
              initial={{ opacity: 0, x: 16 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: 0.08 }}
            >
              <p className="eyebrow">FDA & regulatory</p>
              <div className="section-accent" aria-hidden="true" />
              <h2 id="compliance-heading">Compliance forms, deadlines, and documentation</h2>
              <p>
                Protocol deviations, adverse events, communication records, and notes to file, plus a deadlines view so you stay on track for regulatory readiness and FDA-related workflows.
              </p>
            </MotionBox>
          </div>
        </section>

        {/* Dashboard overview — optional bento wireframe */}
        <section id="dashboard" className="product-platform-section" aria-labelledby="dashboard-heading">
          <div className="product-platform-section-header">
            <p className="eyebrow">Dashboard</p>
            <div className="section-accent" aria-hidden="true" />
            <Heading as="h2" id="dashboard-heading" size="xl" color="gray.900" mb={2}>
              One place for tasks and activity
            </Heading>
            <Text color="gray.600" maxW="560px" mx="auto">
              Overdue tasks, due today, upcoming deadlines, and compliance at a glance, with recent activity so nothing slips.
            </Text>
          </div>
          <MotionBox
            className="product-wireframe-wrapper"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-24px' }}
            transition={{ duration: 0.4 }}
            maxW="640px"
            mx="auto"
          >
            <div className="product-wireframe-bento" aria-hidden="true">
              <div className="product-wireframe-bento-stat product-wireframe-bento-stat-primary">
                <span className="product-wireframe-bento-stat-value">0</span>
                <span className="product-wireframe-bento-stat-label">Overdue Tasks</span>
              </div>
              <div className="product-wireframe-bento-stat product-wireframe-bento-stat-warning">
                <span className="product-wireframe-bento-stat-value">2</span>
                <span className="product-wireframe-bento-stat-label">Due Today</span>
              </div>
              <div className="product-wireframe-bento-stat product-wireframe-bento-stat-success">
                <span className="product-wireframe-bento-stat-value">5</span>
                <span className="product-wireframe-bento-stat-label">Upcoming</span>
              </div>
              <div className="product-wireframe-bento-stat product-wireframe-bento-stat-info">
                <span className="product-wireframe-bento-stat-value">100%</span>
                <span className="product-wireframe-bento-stat-label">Compliance Rate</span>
              </div>
              <div className="product-wireframe-bento-activity">
                <p className="product-wireframe-activity-title">Recent activity</p>
                <p className="product-wireframe-activity-line-text">Protocol deviation submitted for Luna</p>
                <p className="product-wireframe-activity-line-text">Patient Thunder enrolled in Bio-Ply</p>
                <p className="product-wireframe-activity-line-text">Day 0 treatment completed · Luna</p>
              </div>
            </div>
          </MotionBox>
        </section>

        {/* CTA bar */}
        <section className="product-platform-cta-bar" aria-label="Request a demo">
          <div className="product-platform-cta-inner">
            <h2>Ready to run trials?</h2>
            <p>Join veterinarians and sponsors who use the Qentoros platform for equine clinical trials.</p>
            <Button
              size="lg"
              bg="transparent"
              color="white"
              background="linear-gradient(135deg, var(--color-cta-orange) 0%, var(--color-primary-maroon) 100%)"
              _hover={{
                background: 'linear-gradient(135deg, var(--color-cta-orange-hover) 0%, var(--color-primary-dark) 100%)',
                boxShadow: '0 4px 14px rgba(139, 21, 56, 0.35)',
              }}
              borderRadius="var(--radius-md)"
              fontWeight={600}
              onClick={() => navigate('/contact')}
            >
              Request a Demo
            </Button>
          </div>
        </section>

        {/* Footer */}
      </main>

      <LandingFooter />
    </div>
  );
}
